import {
  legacyIsDwbEnabledAtSite,
  legacyIsBlockedForStandaloneDwb,
  legacyIsProgramEnabledAtSite,
} from './legacySiteProgramAccess'

export const SITE_PROGRAM_ENABLED_PREFERENCE_NAMES = {
  dwbp: 'dwbp_enabled',
  k5p: 'k5p_enabled',
  msp: 'msp_enabled',
  hsp: 'hsp_enabled',
  sela: 'sela_enabled',
  cpu: 'cpu_enabled',
  bpu: 'bpu_enabled',
}

/** Program keys mapped to *_enabled preference names (program is encoded in the name prefix). */
export const SITE_PROGRAM_ENABLED_PREFERENCES = {
  DWB: [SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.dwbp],
  K8: [
    SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.k5p,
    SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.msp,
  ],
  HS: [SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.hsp],
  BPU: [SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.bpu],
  CPU: [SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.cpu],
  SELA: [SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.sela],
}

const STANDALONE_DWB_BLOCKER_PREFERENCE_NAMES = [
  SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.hsp,
  SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.k5p,
  SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.msp,
]

export const isTruthyBooleanPreference = value => {
  if (typeof value === 'boolean') return value
  if (value === null || value === undefined) return false
  return value === 'true' || value === true || value === 1
}

/**
 * Reads a *_enabled preference from sites/me `preferences[]` by name.
 * @returns {boolean|null} null when the preference is not present on the site.
 */
export const readSiteLevelPreference = (site, preferenceName) => {
  const preferences = site?.preferences
  if (!Array.isArray(preferences)) return null

  const preference = preferences.find(p => p?.name === preferenceName)
  if (preference === undefined) return null

  return isTruthyBooleanPreference(preference.value)
}

/** True when the named *_enabled preference is present and explicitly enabled. */
export const isSitePreferenceEnabled = (site, preferenceName) =>
  readSiteLevelPreference(site, preferenceName) === true

/**
 * True when any listed *_enabled preference is explicitly enabled.
 * Same OR rule for every program (including K8 across k5p_enabled and msp_enabled).
 */
export const isAnySitePreferenceEnabled = (site, preferenceNames = []) =>
  preferenceNames.some(name => isSitePreferenceEnabled(site, name))

const getEnabledPreferenceNamesForProgram = programKey =>
  SITE_PROGRAM_ENABLED_PREFERENCES[programKey] || []

/** @deprecated Prefer isAnySitePreferenceEnabled with SITE_PROGRAM_ENABLED_PREFERENCES[programKey]. */
export const isProgramEnabledViaSitePreferences = (site, programKey) =>
  isAnySitePreferenceEnabled(
    site,
    getEnabledPreferenceNamesForProgram(programKey),
  )

/**
 * A program is enabled when any *_enabled preference for that program is true
 * OR legacy license/capability rules say the program is enabled.
 */
export const isProgramEnabledAtSite = (site, programKey) =>
  isProgramEnabledViaSitePreferences(site, programKey) ||
  legacyIsProgramEnabledAtSite(site, programKey)

export const isDwbEnabledAtSite = site =>
  isAnySitePreferenceEnabled(site, [
    SITE_PROGRAM_ENABLED_PREFERENCE_NAMES.dwbp,
  ]) || legacyIsDwbEnabledAtSite(site)

const isStandaloneDwbBlockedAtSite = site =>
  isAnySitePreferenceEnabled(site, STANDALONE_DWB_BLOCKER_PREFERENCE_NAMES) ||
  legacyIsBlockedForStandaloneDwb(site)

export const isStandaloneDwbEligibleSite = site =>
  isProgramEnabledAtSite(site, 'DWB') && !isStandaloneDwbBlockedAtSite(site)
